import {
  FeatureName,
  IncomingCall,
  IssueType,
  PermissionValues,
  useCallUpdates,
  useCreateIssueWithMessage,
  useCurrentAccount,
  useEndCall,
  useFetchActiveCallForIssue,
  useGrippContext,
  useIncomingCall,
  useIsFeatureEnabled,
  useJoinActivityCall,
  useLeaveActivityCall,
  useRespondToCall,
  useRingActivityCall,
  useStartCall,
} from '@gripp/shared-logic';
import { useSystemNotification } from '@gripp/shared-ui';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CallRoom } from './CallRoom';
import { IncomingCallModal } from './IncomingCallModal';

/** A person who can be called — id plus a display name for the call UI. */
export interface Callee {
  id: string;
  name: string;
}

interface CallContextValue {
  /** Place a call to another account in the current workspace. */
  startCall: (callee: Callee) => void;
  /**
   * Start a call from an Asset: create a red "Call" Activity, ring every
   * Workspace Admin, and join the room as caller. `undefined` when the current
   * account lacks `call.create` — call sites should treat an absent
   * `startAssetCall` as "calling not available" and hide their trigger, matching
   * the backend gate so the user never hits a FORBIDDEN dead-end.
   */
  startAssetCall?: (asset: {
    assetId: string;
    assetName: string;
    workspaceId: string;
  }) => void;
  /** Start or join the group call on an Activity (Issue) — no ringing. */
  joinActivityCall: (issueId: string, title: string) => void;
  /** True while a call is ringing, connecting, or active. */
  isBusy: boolean;
}

const CallContext = createContext<CallContextValue | undefined>(undefined);

const TERMINAL_STATES = ['DECLINED', 'ENDED', 'CANCELLED'];

/**
 * Backstop: never ring forever. If every server teardown signal is missed
 * (plan 0025-inbound-call-lifecycle-hardening §9), dismiss the ring locally
 * and move on to any stashed ring.
 */
const RING_TIMEOUT_MS = 60_000;

const fullName = (name?: { first?: string; last?: string }): string =>
  [name?.first, name?.last].filter(Boolean).join(' ').trim() || 'Unknown';

const initialsOf = (name?: { first?: string; last?: string }): string =>
  `${name?.first?.[0] ?? ''}${name?.last?.[0] ?? ''}`.toUpperCase() || '?';

interface ActiveCall {
  /** Present only for 1:1 calls; activity (ring-all) calls have no single callId. */
  callId?: string;
  room: string;
  token: string;
  url: string;
  peerName: string;
  role: 'caller' | 'callee';
  /** Present only for asset/activity calls; drives the on-hangup redirect. */
  issueId?: string;
  /** 'direct' = 1:1 layout (ring-based); 'group' = join-based Activity grid. */
  mode: 'direct' | 'group';
  /** Header title for group calls (the Activity title). */
  title?: string;
}

export const CallProvider = ({
  children,
  navigate,
}: {
  children: ReactNode;
  /**
   * Imperative redirect. CallProvider is mounted ABOVE `RouterProvider`, so
   * `useNavigate` is unavailable here — `AuthenticatedBootstrap` threads the
   * `router` singleton's `.navigate` in as this prop.
   */
  navigate?: (path: string) => void;
}) => {
  const { logger } = useGrippContext();
  const { showError } = useSystemNotification();
  const { account } = useCurrentAccount();
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);

  // `useSubscription` registers its handler once (effect deps are [auth,
  // enabled]) and never re-binds it, so the useIncomingCall callback below
  // closes over a stale `active`. Mirror it into a ref the callback can read
  // live, or a new ring would slip through the busy guard mid-call.
  const activeRef = useRef<ActiveCall | null>(null);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // The `active || incoming` guards below only take effect once the mutation
  // that sets them resolves — a fast double-tap fires the initiator twice
  // before either state update lands. This ref is set synchronously (before
  // any await) so the second tap is blocked immediately, and cleared in a
  // `finally` once the async work settles on every path.
  const startingRef = useRef(false);

  // Match the resolver gate (Call.create runs through PermissionProxyService):
  // only accounts with `call.create` may originate a call.
  const canCall = !!account?.hasPermission(PermissionValues.call.create);

  const reportCallError = useCallback(
    (err: unknown, fallback: string) => {
      logger?.error(err);
      const message = err instanceof Error ? err.message : fallback;
      showError('Call failed', message);
    },
    [logger, showError]
  );

  const startCallMutation = useStartCall();
  const respondMutation = useRespondToCall();
  const endCallMutation = useEndCall();
  const createIssueMutation = useCreateIssueWithMessage();
  const ringActivityCallMutation = useRingActivityCall();
  const leaveActivityCallMutation = useLeaveActivityCall();
  const joinActivityMutation = useJoinActivityCall();

  // Only receive rings when the feature is enabled — initiation is already
  // flag-gated at each entry point, so this keeps the whole feature (send AND
  // receive) behind COMMUNICATION_PLATFORM.
  const isCommunicationPlatformEnabled = useIsFeatureEnabled(
    FeatureName.CommunicationPlatform
  );

  // Rings that arrived while another ring was showing. `newCall` is one-shot —
  // a dropped ring can never ring this client again — so stash and present
  // once the current ring clears (see presentNextPendingRing).
  const pendingRingsRef = useRef<IncomingCall[]>([]);

  // Idempotent (dedup by id): called from inside a state updater, which React
  // may invoke more than once. Only Activity-backed rings are stashed — they
  // are the only ones re-verifiable via activeCallForIssue.
  const stashRing = useCallback((call: IncomingCall) => {
    if (!call.issue?.id) {
      return;
    }
    if (pendingRingsRef.current.some((pending) => pending.id === call.id)) {
      return;
    }
    pendingRingsRef.current.push(call);
  }, []);

  const fetchActiveCallForIssue = useFetchActiveCallForIssue();

  // Present the newest stashed ring that is still RINGING server-side. The
  // live check is required: presenting a dead ring would stick (its terminal
  // `updatedCall` fired before it was on screen), and ACTIVE means someone
  // else answered (answer-first-wins).
  const presentNextPendingRing = useCallback(async () => {
    while (pendingRingsRef.current.length > 0) {
      const candidate = pendingRingsRef.current.pop();
      if (!candidate?.issue?.id) {
        continue;
      }
      try {
        const live = await fetchActiveCallForIssue(candidate.issue.id);
        if (live?.state === 'RINGING' && live.room === candidate.room) {
          if (!activeRef.current) {
            setIncoming((current) => current ?? candidate);
          }
          return;
        }
      } catch (err) {
        logger?.error(err);
      }
    }
  }, [fetchActiveCallForIssue, logger]);

  // Ring when someone calls us — ignore if already busy on another call
  // (`activeRef` reads the live value; see the ref note above). A ring that
  // arrives while a DIFFERENT ring is showing is stashed, not dropped.
  useIncomingCall(async (call) => {
    if (activeRef.current) {
      return;
    }
    setIncoming((current) => {
      if (current && current.id !== call.id) {
        stashRing(call);
        return current;
      }
      return current ?? call;
    });
  }, isCommunicationPlatformEnabled);

  // Watch the active call for remote transitions (callee declined, either
  // party hung up) and tear the room down when it reaches a terminal state.
  useCallUpdates(active?.callId, async (update) => {
    if (TERMINAL_STATES.includes(update.state)) {
      setActive(null);
    }
  });

  // Losing-ringer dismiss: clear the ring when the call ends remotely, then
  // surface any ring stashed behind it.
  useCallUpdates(incoming?.id, async (update) => {
    if (TERMINAL_STATES.includes(update.state)) {
      setIncoming(null);
      await presentNextPendingRing();
    }
  });

  // Ring-timeout backstop (see RING_TIMEOUT_MS); timer cleared on unmount and
  // on every incoming transition.
  useEffect(() => {
    if (!incoming) {
      return;
    }
    const timer = setTimeout(() => {
      setIncoming(null);
      presentNextPendingRing().catch((err) => logger?.error(err));
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [incoming, presentNextPendingRing, logger]);

  const startCall = useCallback(
    (callee: Callee) => {
      if (active || incoming || startingRef.current) {
        return;
      }
      startingRef.current = true;
      startCallMutation
        .mutateAsync(callee.id)
        .then((result) => {
          if (!result.token) {
            return;
          }
          setActive({
            callId: result.call.id,
            room: result.call.room,
            token: result.token,
            url: result.url,
            peerName: callee.name,
            role: 'caller',
            mode: 'direct',
          });
        })
        .catch((err) => reportCallError(err, `Couldn't call ${callee.name}`))
        .finally(() => {
          startingRef.current = false;
        });
    },
    [active, incoming, startCallMutation, reportCallError]
  );

  const startAssetCall = useCallback(
    (asset: { assetId: string; assetName: string; workspaceId: string }) => {
      if (active || incoming || startingRef.current) {
        return;
      }
      startingRef.current = true;
      (async () => {
        const created = (await createIssueMutation.mutateAsync({
          input: {
            workspaceId: asset.workspaceId,
            severity: 'red',
            title: 'Call',
            type: IssueType.Report,
            message: `Call started on ${asset.assetName}`,
            assetId: asset.assetId,
          },
        })) as { issue: { reportIssue: { id: string } } };
        const issueId = created.issue.reportIssue.id;
        const result = await ringActivityCallMutation.mutateAsync(issueId);
        if (!result.token) {
          return;
        }
        setActive({
          callId: result.call.id,
          room: result.call.room,
          token: result.token,
          url: result.url,
          peerName: asset.assetName,
          role: 'caller',
          // An asset call rings every admin and anyone can join — it's a group
          // call from the start, so use the group grid (self PiP + "Waiting for
          // others…") rather than the 1:1 "Ringing…" screen.
          mode: 'group',
          title: asset.assetName,
          issueId,
        });
      })()
        .catch((err) =>
          reportCallError(err, `Couldn't start a call on ${asset.assetName}`)
        )
        .finally(() => {
          startingRef.current = false;
        });
    },
    [
      active,
      incoming,
      createIssueMutation,
      ringActivityCallMutation,
      reportCallError,
    ]
  );

  const joinActivityCall = useCallback(
    (issueId: string, title: string) => {
      if (active || incoming || startingRef.current) {
        return;
      }
      startingRef.current = true;
      joinActivityMutation
        .mutateAsync(issueId)
        .then((result) => {
          if (!result.token) {
            return;
          }
          setActive({
            callId: result.call.id,
            room: result.call.room,
            token: result.token,
            url: result.url,
            peerName: title,
            role: 'callee', // no ringing for group calls
            mode: 'group',
            title,
            issueId,
          });
        })
        .catch((err) => reportCallError(err, `Couldn't join the call`))
        .finally(() => {
          startingRef.current = false;
        });
    },
    [active, incoming, joinActivityMutation, reportCallError]
  );

  const acceptIncoming = useCallback(() => {
    if (!incoming || startingRef.current) {
      return;
    }
    startingRef.current = true;
    respondMutation
      .mutateAsync({ callId: incoming.id, accept: true })
      .then((result) => {
        // Answering makes us busy; anything stashed would be stale by hang-up.
        pendingRingsRef.current = [];
        if (result.token) {
          // An activity (group) call carries an `issue`; a 1:1 call doesn't.
          // Answering a group ring must enter the group grid AND carry the
          // issueId so hang-up leaves via leaveActivityCall (per-participant)
          // rather than ending a single 1:1 row.
          const groupIssueId = incoming.issue?.id;
          setActive({
            callId: incoming.id,
            room: incoming.room,
            token: result.token,
            url: result.url,
            peerName: fullName(incoming.caller.name),
            role: 'callee',
            mode: groupIssueId ? 'group' : 'direct',
            title: groupIssueId ? fullName(incoming.caller.name) : undefined,
            issueId: groupIssueId,
          });
        }
        setIncoming(null);
      })
      .catch((err) => {
        // Accept failed (commonly: the call died as it was answered).
        reportCallError(err, 'Could not accept the call');
        setIncoming(null);
        presentNextPendingRing().catch((e) => logger?.error(e));
      })
      .finally(() => {
        startingRef.current = false;
      });
  }, [incoming, respondMutation, reportCallError, presentNextPendingRing, logger]);

  const declineIncoming = useCallback(() => {
    if (!incoming) {
      return;
    }
    const callId = incoming.id;
    setIncoming(null);
    respondMutation
      .mutateAsync({ callId, accept: false })
      .catch((err) => logger?.error(err));
    presentNextPendingRing().catch((err) => logger?.error(err));
  }, [incoming, respondMutation, logger, presentNextPendingRing]);

  const hangUp = useCallback(() => {
    if (!active) {
      return;
    }
    const { callId, issueId } = active;
    setActive(null);
    if (issueId) {
      // Asset/group call: leave only the local participant's own row (other
      // participants' rows — and the room, for a group call — carry on). The
      // caller of an asset call still drops onto the Activity to see the result.
      leaveActivityCallMutation
        .mutateAsync(issueId)
        .catch((err) => logger?.error(err));
      navigate?.(`/activities/${issueId}`);
      return;
    }
    if (callId) {
      endCallMutation.mutateAsync(callId).catch((err) => logger?.error(err));
    }
  }, [active, leaveActivityCallMutation, endCallMutation, navigate, logger]);

  const value = useMemo<CallContextValue>(
    () => ({
      startCall,
      startAssetCall: canCall ? startAssetCall : undefined,
      joinActivityCall,
      isBusy: active != null || incoming != null,
    }),
    [startCall, startAssetCall, joinActivityCall, canCall, active, incoming]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      {incoming && (
        <IncomingCallModal
          callerName={fullName(incoming.caller.name)}
          callerInitials={initialsOf(incoming.caller.name)}
          onAccept={acceptIncoming}
          onDecline={declineIncoming}
          responding={respondMutation.isPending}
        />
      )}
      {active && (
        <CallRoom
          url={active.url}
          token={active.token}
          peerName={active.peerName}
          role={active.role}
          mode={active.mode}
          title={active.title}
          onDisconnected={hangUp}
        />
      )}
    </CallContext.Provider>
  );
};

/**
 * Access the call controller. Returns undefined outside CallProvider — call
 * sites should optional-chain so non-authenticated trees stay inert.
 */
export const useCall = (): CallContextValue | undefined => useContext(CallContext);
